import React, { ReactNode, useEffect } from 'react'
import { twJoin, twMerge } from 'tailwind-merge'
import { Tooltip } from 'shared/components/tooltip'
import { LimitType } from 'shared/types/swr-pagination-interface'
import EmptyList from './empty-list'
import TableLimitPagination, {
  DataWithPaginationInterface,
  PaginationInterface,
} from './table-limit-pagination'

export interface ColumnDefinitionInterface<T> {
  key: keyof T | T[keyof T]
  header: string | JSX.Element
  widthPercent?: number
  onRender?: (instance: T) => JSX.Element
  onRenderSmall?: (instance: T) => JSX.Element
  actionsCount?: number
}

export interface TableProps<T> {
  data: DataWithPaginationInterface<T> | undefined
  columns: ColumnDefinitionInterface<T>[]
  limiter?: {
    limit: LimitType
    setLimit: React.Dispatch<React.SetStateAction<LimitType>>
  }
  paginator?: {
    pagination: PaginationInterface
    setPagination: React.Dispatch<React.SetStateAction<PaginationInterface>>
    hasMore?: boolean
  }
  testAttributePostfix?: string
  className?: string
}

interface TableRowsProps<T>
  extends Pick<TableProps<T>, 'data' | 'columns' | 'testAttributePostfix'> {}

interface TableHeaderProps<T> extends Pick<TableProps<T>, 'columns'> {}

interface TableSkeletonProps<T> {
  columns: ColumnDefinitionInterface<T>[]
  totalWidth: number
}

export function Table<T extends { id: number | string }>({
  data,
  columns,
  limiter,
  paginator,
  testAttributePostfix,
  className,
}: TableProps<T>) {
  if (!data || !data.items.length) {
    return <EmptyList />
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <div
        className={twMerge('overflow-x-hidden rounded-lg', className)}
        data-test-table-is-loaded={!!data}
      >
        <TableHeader columns={columns} />
        <TableRows data={data} columns={columns} testAttributePostfix={testAttributePostfix} />
      </div>
      {paginator && (
        <div className="flex items-center justify-end gap-5 sm:gap-10">
          {limiter && <TableLimitPagination paginator={paginator} limiter={limiter} data={data} />}
        </div>
      )}
    </div>
  )
}

function TableHeader<T>({ columns }: TableHeaderProps<T>) {
  const totalWidth = columns
    .filter(c => c.widthPercent !== undefined)
    .reduce((prev, cur) => prev + cur.widthPercent!, 0)

  const headers = columns.map((column, index) => {
    if (column.key === 'action') {
      const actionsWidth = 35 * column.actionsCount! + 33
      return (
        <div
          key={`headCell-${index}`}
          className="hidden w-[var(--width)] px-6 py-4 text-start font-inter text-[17px] font-bold text-text-primary lg:table-cell"
          style={
            {
              '--width': `${actionsWidth}px`,
            } as React.CSSProperties
          }
        >
          {typeof column.header === 'string' ? (
            <Tooltip mode="overflow" label={column.header}>
              <span className="truncate">{column.header}</span>
            </Tooltip>
          ) : (
            column.header
          )}
        </div>
      )
    }

    return (
      <div
        key={`headCell-${index}`}
        className={`hidden flex-[var(--flex)] truncate px-6 py-4 text-start font-inter text-[17px] font-bold text-text-primary lg:table-cell`}
        style={
          {
            '--flex': `${((column.widthPercent ?? 25) / totalWidth) * 100}`,
          } as React.CSSProperties
        }
      >
        {typeof column.header === 'string' ? (
          <Tooltip mode="overflow" label={column.header}>
            <span className="truncate">{column.header}</span>
          </Tooltip>
        ) : (
          column.header
        )}
      </div>
    )
  })

  return <div className="flex items-center bg-brand-primary-subtle">{headers}</div>
}

function TableRows<T extends { id: number | string }>({
  data,
  columns,
  testAttributePostfix,
}: TableRowsProps<T>) {
  const totalWidth = columns
    .filter(c => c.widthPercent !== undefined)
    .reduce((prev, cur) => prev + cur.widthPercent!, 0)

  if (data === undefined) {
    return <TableSkeleton columns={columns} totalWidth={totalWidth} />
  }

  return (
    <div>
      {data.items.map((row, index) => (
        <div
          key={`row-${index}`}
          className="group flex flex-col items-start gap-4 px-6 py-4 odd:bg-background-secondary even:bg-brand-primary-subtle lg:flex-row lg:items-center lg:gap-0 lg:p-0"
          {...(testAttributePostfix
            ? {
                [`data-test-${testAttributePostfix}`]: `${testAttributePostfix}-item-${row.id}`,
              }
            : {})}
        >
          {columns.map((column, index2) => {
            if (!column.widthPercent && column.key !== 'action') column.widthPercent = 25
            const keys = (column.key as string).split('.')
            let content: T[keyof T] | T | undefined = undefined
            keys.forEach(key => {
              content = !!content ? (content as T)[key as keyof T] : row[key as keyof T]
            })
            const onRenderSmall = column.onRenderSmall
              ? column.onRenderSmall(row)
              : column.onRender
                ? column.onRender(row)
                : content

            const onRender = column.onRender ? column.onRender(row) : content
            const node = (
              <>
                <div
                  className={twJoin(
                    `flex items-center lg:hidden`,
                    column.key !== 'action' && 'gap-3',
                  )}
                >
                  {column.onRenderSmall ? (
                    onRenderSmall
                  ) : (
                    <>
                      <span className="whitespace-nowrap font-bold text-text-primary">
                        {column.header}
                      </span>
                      {onRender}
                    </>
                  )}
                </div>

                <div className="hidden lg:flex">{onRender}</div>
              </>
            )
            return (
              <div
                key={`cell-${index2}`}
                className={twJoin(
                  'lg:px-6 lg:py-4',
                  column.key === 'action'
                    ? 'w-fit group-focus-within:opacity-100 group-hover:opacity-100 lg:opacity-0'
                    : `w-full lg:w-0`,
                )}
                style={{
                  flex: `${
                    column.key === 'action' ? '0' : `${(column.widthPercent! / totalWidth) * 100}`
                  }`,
                }}
              >
                {node as ReactNode}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

function TableSkeleton<T>({ columns, totalWidth }: TableSkeletonProps<T>) {
  return (
    <>
      {Array.from(Array(10).keys()).map((row, index) => (
        <div
          key={`row-${index}`}
          className="group flex animate-pulse flex-col items-start gap-4 px-6 py-4 odd:bg-background-secondary even:bg-brand-primary-subtle lg:flex-row lg:items-center lg:gap-0 lg:p-0"
        >
          {columns.map((column, index2) => {
            if (!column.widthPercent) column.widthPercent = 25
            let actionsWidth = 0
            if (column.key === 'action') {
              actionsWidth = 35 * column.actionsCount! + 33
            }
            return (
              <div
                className={`lg:px-6 lg:py-4 ${column.key === 'action' ? '' : `w-full`}`}
                key={`cell-${index2}`}
                style={{
                  width: `${column.key === 'action' && `${actionsWidth}px`}`,
                  flex: `${
                    column.key !== 'action' && `${(column.widthPercent / totalWidth) * 100}`
                  }`,
                }}
              >
                <div className="rounded bg-skeleton-primary text-transparent">.</div>
              </div>
            )
          })}
        </div>
      ))}
    </>
  )
}
